import type { CaptureMetadata } from "@shared/types/annotations";
import type { EntryCollection } from "@/types/entryCollection";
import type {
  GradingBucket,
  GradingBucketRange,
  ImageMeasurementGrading,
} from "@/types/entryConfig";

const getEntryConfigId = (entryCollection: EntryCollection): string =>
  typeof entryCollection.entryConfigRef === "string"
    ? entryCollection.entryConfigRef
    : entryCollection.entryConfigRef.id;

/**
 * Per-plot distribution of detected objects across the configured grading
 * buckets. `counts` keys are bucket labels (every configured bucket is
 * always present, possibly with count 0). `ungraded` counts visible objects
 * that didn't match any bucket — either because they fell into a gap, or
 * because they were missing the chosen dimension's measurement.
 */
export interface GradeDistribution {
  counts: Record<string, number>;
  ungraded: number;
  totalObjects: number;
  dominantLabel: string | null;
  dominantFraction: number;
}

const rangeContains = (range: GradingBucketRange, value: number): boolean => {
  switch (range.kind) {
    case "lessThan":
      return value < range.maxMeters;
    case "between":
      return value >= range.minMeters && value < range.maxMeters;
    case "greaterThanOrEqual":
      return value >= range.minMeters;
  }
};

/**
 * Find the first bucket whose range contains `valueMeters`, or `null` if
 * none match. Buckets are scanned in the order they appear in
 * `grading.buckets`; for non-overlapping configurations (the form enforces
 * this) at most one bucket matches.
 */
export function assignBucket(
  valueMeters: number,
  grading: ImageMeasurementGrading
): GradingBucket | null {
  if (!Number.isFinite(valueMeters)) return null;
  for (const bucket of grading.buckets) {
    if (rangeContains(bucket.range, valueMeters)) return bucket;
  }
  return null;
}

/**
 * Bucket every visible annotation across `metadataList` by the grading
 * dimension, returning the distribution + dominant grade.
 *
 * Returns `null` when grading has no buckets or there are no visible
 * objects to grade (so callers can render "--" instead of a misleading
 * empty distribution).
 */
export function computeGradeDistribution(
  metadataList: CaptureMetadata[],
  grading: ImageMeasurementGrading
): GradeDistribution | null {
  if (grading.buckets.length === 0) return null;

  const counts: Record<string, number> = {};
  for (const bucket of grading.buckets) counts[bucket.label] = 0;
  let ungraded = 0;
  let totalObjects = 0;

  for (const metadata of metadataList) {
    const annotations = metadata.annotations?.annotations ?? [];
    for (const annotation of annotations) {
      if (!annotation.userVisibility?.isVisible) continue;
      totalObjects += 1;
      const value =
        grading.dimension === "length"
          ? annotation.measurements?.lengthM
          : annotation.measurements?.widthM;
      if (value === undefined || value === null) {
        ungraded += 1;
        continue;
      }
      const bucket = assignBucket(value, grading);
      if (bucket) {
        counts[bucket.label] += 1;
      } else {
        ungraded += 1;
      }
    }
  }

  if (totalObjects === 0) return null;

  let dominantLabel: string | null = null;
  let dominantCount = 0;
  for (const bucket of grading.buckets) {
    const count = counts[bucket.label];
    if (count > dominantCount) {
      dominantCount = count;
      dominantLabel = bucket.label;
    }
  }
  const dominantFraction = dominantCount === 0 ? 0 : dominantCount / totalObjects;

  return { counts, ungraded, totalObjects, dominantLabel, dominantFraction };
}

/**
 * Per-plot aggregator. Walks `entryCollections`, filters to image-measurement
 * collections for `entryConfigId`, resolves each entry's
 * `captureMetadataCloudPath` against `metadataByPath`, and reduces the
 * resulting metadata list via `computeGradeDistribution`.
 *
 * Callers (heat map) own the metadata cache. Entries whose metadata is not in
 * the map are silently skipped so an in-flight metadata fetch does not block
 * partial rendering; the caller is responsible for triggering the fetch and
 * showing a loading state at the cell or tooltip level.
 */
export function getImageMeasurementGradeDistribution(
  entryCollections: EntryCollection[],
  entryConfigId: string,
  grading: ImageMeasurementGrading,
  metadataByPath: Map<string, CaptureMetadata>
): GradeDistribution | null {
  const matchingCollections = entryCollections.filter(
    (c) => getEntryConfigId(c) === entryConfigId && c.type === "imageMeasurement"
  );
  if (matchingCollections.length === 0) return null;

  const metadataList: CaptureMetadata[] = [];
  for (const collection of matchingCollections) {
    const entries = collection.imageMeasurementEntries
      ? Object.values(collection.imageMeasurementEntries)
      : [];
    for (const entry of entries) {
      const path = entry.captureMetadataCloudPath;
      if (!path) continue;
      const metadata = metadataByPath.get(path);
      if (metadata) metadataList.push(metadata);
    }
  }
  if (metadataList.length === 0) return null;
  return computeGradeDistribution(metadataList, grading);
}

// MARK: - Form-time validation

/**
 * Form-state shape for an in-progress bucket. `minCm` / `maxCm` are stored
 * as numbers (parsed at the input boundary) and may be undefined while the
 * user is mid-edit. Unit is centimeters because that is what the UI shows;
 * conversion to meters happens at submit time.
 */
export interface GradingBucketDraft {
  id: string;
  label: string;
  kind: "lessThan" | "between" | "greaterThanOrEqual";
  minCm?: number;
  maxCm?: number;
}

export interface GradingValidationResult {
  /** Per-bucket error keyed by draft `id`. Missing key means the bucket is valid. */
  bucketErrors: Record<string, string>;
  /** Cross-bucket overlap message, naming the conflicting labels. */
  overlapError: string | null;
  /** Missing coverage message when the buckets do not cover all positive values. */
  coverageError: string | null;
  isValid: boolean;
}

const MAX_LABEL_LENGTH = 32;
const RESERVED_LABELS = new Set(["ungraded"]);

const normalizeLabel = (label: string) => label.trim().toLowerCase();
const formatCmForMessage = (value: number): string => value.toFixed(2);

const validateSingleBucket = (
  draft: GradingBucketDraft
): string | null => {
  const trimmed = draft.label.trim();
  if (!trimmed) return "Grade is required";
  if (trimmed.length > MAX_LABEL_LENGTH) {
    return `Grade must be ${MAX_LABEL_LENGTH} characters or fewer`;
  }
  if (RESERVED_LABELS.has(normalizeLabel(trimmed))) {
    return `"${trimmed}" is reserved for objects that do not match a bucket`;
  }
  switch (draft.kind) {
    case "lessThan":
      if (draft.maxCm === undefined || !(draft.maxCm > 0)) {
        return "Enter a value greater than 0";
      }
      return null;
    case "greaterThanOrEqual":
      if (draft.minCm === undefined || !(draft.minCm > 0)) {
        return "Enter a value greater than 0";
      }
      return null;
    case "between":
      if (draft.minCm === undefined || draft.maxCm === undefined) {
        return "Enter both min and max";
      }
      if (!(draft.minCm > 0) || !(draft.maxCm > 0)) {
        return "Values must be greater than 0";
      }
      if (!(draft.minCm < draft.maxCm)) {
        return "Min must be less than max";
      }
      return null;
  }
};

interface NumericInterval {
  lo: number; // -Infinity allowed
  hi: number; // +Infinity allowed
  draft: GradingBucketDraft;
}

const draftToInterval = (draft: GradingBucketDraft): NumericInterval | null => {
  switch (draft.kind) {
    case "lessThan":
      return draft.maxCm !== undefined
        ? { lo: -Infinity, hi: draft.maxCm, draft }
        : null;
    case "greaterThanOrEqual":
      return draft.minCm !== undefined
        ? { lo: draft.minCm, hi: Infinity, draft }
        : null;
    case "between":
      return draft.minCm !== undefined && draft.maxCm !== undefined
        ? { lo: draft.minCm, hi: draft.maxCm, draft }
        : null;
  }
};

/**
 * Validate an array of bucket drafts as a unit:
 *   - per-bucket: label present + within length, numeric values present & positive,
 *     between has min < max.
 *   - cross-bucket: labels unique (case- and whitespace-insensitive), intervals
 *     non-overlapping (half-open `[lo, hi)`).
 *
 * Overlap reporting is suppressed if any bucket already has a value error,
 * because the user is still mid-edit and a follow-on overlap message would be
 * noise.
 */
export function validateGradingBuckets(
  drafts: GradingBucketDraft[]
): GradingValidationResult {
  const bucketErrors: Record<string, string> = {};

  for (const draft of drafts) {
    const error = validateSingleBucket(draft);
    if (error) bucketErrors[draft.id] = error;
  }

  // Duplicate grade detection (uses normalized form, ignores blanks since
  // those already errored out above).
  const seen = new Map<string, string>();
  for (const draft of drafts) {
    if (bucketErrors[draft.id]) continue;
    const key = normalizeLabel(draft.label);
    if (!key) continue;
    if (seen.has(key)) {
      bucketErrors[draft.id] = "Duplicate grade";
    } else {
      seen.set(key, draft.id);
    }
  }

  let overlapError: string | null = null;
  let coverageError: string | null = null;
  const hasAnyError = Object.keys(bucketErrors).length > 0;
  let intervals: NumericInterval[] = [];
  if (!hasAnyError) {
    intervals = drafts
      .map(draftToInterval)
      .filter((i): i is NumericInterval => i !== null)
      .sort((a, b) => a.lo - b.lo);
    for (let i = 0; i + 1 < intervals.length; i += 1) {
      if (intervals[i + 1].lo < intervals[i].hi) {
        overlapError = `Ranges for "${intervals[i].draft.label.trim()}" and "${intervals[i + 1].draft.label.trim()}" overlap. Adjust their bounds so every value falls in exactly one bucket.`;
        break;
      }
    }
  }

  if (!hasAnyError && !overlapError && intervals.length > 0) {
    if (intervals[0].lo > 0) {
      coverageError = `Grades do not cover measurements below ${formatCmForMessage(intervals[0].lo)} cm. Add a "Less than" grade so every measured object receives a grade.`;
    } else {
      for (let i = 0; i + 1 < intervals.length; i += 1) {
        if (intervals[i + 1].lo > intervals[i].hi) {
          coverageError = `Grades do not cover measurements between ${formatCmForMessage(intervals[i].hi)} cm and ${formatCmForMessage(intervals[i + 1].lo)} cm. Adjust the grade ranges so every measured object receives a grade.`;
          break;
        }
      }
      const last = intervals[intervals.length - 1];
      if (!coverageError && last.hi !== Infinity) {
        coverageError = `Grades do not cover measurements ${formatCmForMessage(last.hi)} cm and above. Add a "Greater or equal" grade so every measured object receives a grade.`;
      }
    }
  }

  return {
    bucketErrors,
    overlapError,
    coverageError,
    isValid: !hasAnyError && overlapError === null && coverageError === null,
  };
}

// 4 decimal places (0.0001 cm = 1µm). Enough to represent inch-based grade
// standards in the editor — e.g. 1.0625 in = 2.69875 cm — which 2dp would crush to
// 2.70 cm / "1.06 in". Grading-only (used by the grade editor + draft<->meters here).
export const roundCm = (cm: number): number => Math.round(cm * 10000) / 10000;
const cmToMeters = (cm: number): number => roundCm(cm) / 100;
const metersToCm = (m: number): number =>
  // Round to the grading cm precision (4dp) for centimeter form display.
  roundCm(m * 100);

/**
 * Convert validated form drafts to the persisted `ImageMeasurementGrading`
 * shape. Centimeter inputs become meter-valued ranges; labels are trimmed.
 * Throws if a draft is missing a required value — the form's validation
 * pass must run first.
 */
export function convertDraftsToGrading(
  dimension: ImageMeasurementGrading["dimension"],
  drafts: GradingBucketDraft[]
): ImageMeasurementGrading {
  const buckets: GradingBucket[] = drafts.map((draft) => {
    let range: GradingBucketRange;
    switch (draft.kind) {
      case "lessThan":
        if (draft.maxCm === undefined) {
          throw new Error(`Bucket ${draft.id} ("${draft.label}") missing maxCm`);
        }
        range = { kind: "lessThan", maxMeters: cmToMeters(draft.maxCm) };
        break;
      case "greaterThanOrEqual":
        if (draft.minCm === undefined) {
          throw new Error(`Bucket ${draft.id} ("${draft.label}") missing minCm`);
        }
        range = {
          kind: "greaterThanOrEqual",
          minMeters: cmToMeters(draft.minCm),
        };
        break;
      case "between":
        if (draft.minCm === undefined || draft.maxCm === undefined) {
          throw new Error(
            `Bucket ${draft.id} ("${draft.label}") missing minCm/maxCm`
          );
        }
        range = {
          kind: "between",
          minMeters: cmToMeters(draft.minCm),
          maxMeters: cmToMeters(draft.maxCm),
        };
        break;
    }
    return { id: draft.id, label: draft.label.trim(), range };
  });
  return { dimension, buckets };
}

/**
 * Inverse of `convertDraftsToGrading`: build form drafts from a persisted
 * grading config so an existing trait can be edited.
 */
export function gradingToDrafts(
  grading: ImageMeasurementGrading
): GradingBucketDraft[] {
  return grading.buckets.map((bucket) => {
    switch (bucket.range.kind) {
      case "lessThan":
        return {
          id: bucket.id,
          label: bucket.label,
          kind: "lessThan",
          maxCm: metersToCm(bucket.range.maxMeters),
        };
      case "greaterThanOrEqual":
        return {
          id: bucket.id,
          label: bucket.label,
          kind: "greaterThanOrEqual",
          minCm: metersToCm(bucket.range.minMeters),
        };
      case "between":
        return {
          id: bucket.id,
          label: bucket.label,
          kind: "between",
          minCm: metersToCm(bucket.range.minMeters),
          maxCm: metersToCm(bucket.range.maxMeters),
        };
    }
  });
}
